import {
  AFTERSHIP_ADS_GLOBAL_TIMEOUT_MS,
  AFTERSHIP_ADS_SCRIPT_ID,
  AFTERSHIP_ADS_SCRIPT_SRC,
} from '../config';
import { logError, logInfo } from '../logging';
import type { AfterShipAdsLoggerLike, AstagGlobal } from '../types';

/**
 * In-flight/settled load promise, shared across every slot on the page so two slots never
 * inject the tag twice. Reset by `removeScript` so a consent revoke → re-grant can reload.
 */
let loadPromise: Promise<AstagGlobal | null> | null = null;

/**
 * Bumped whenever the tag is torn out. An attempt that is still in flight when that
 * happens is obsolete: it must not clean up by the shared script id, or it would delete
 * the tag a later attempt has since installed.
 */
let loadGeneration = 0;

/** Appends the <script> tag for the AfterShip Ads Web SDK if not present. */
export function injectScript(logger: AfterShipAdsLoggerLike = console): HTMLScriptElement | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const existing = document.getElementById(AFTERSHIP_ADS_SCRIPT_ID);
  if (existing) return existing as HTMLScriptElement;

  try {
    const script = document.createElement('script');
    script.id = AFTERSHIP_ADS_SCRIPT_ID;
    script.src = AFTERSHIP_ADS_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
    logInfo(logger, 'injectScript', 'Script injected');
    return script;
  } catch (err) {
    logError(logger, 'injectScript', 'Failed to inject script', err);
    return null;
  }
}

/**
 * Removes the <script> tag if it exists.
 *
 * This does not unload an already-executed `window.Astag`; it exists so a consent
 * revocation leaves no AfterShip tag in the document. A later re-grant reuses the
 * existing global if available; removing the tag cannot undo vendor side effects.
 */
export function removeScript(logger: AfterShipAdsLoggerLike = console): void {
  if (typeof document === 'undefined') return;

  loadPromise = null;
  loadGeneration += 1;

  try {
    const script = document.getElementById(AFTERSHIP_ADS_SCRIPT_ID);
    if (script) {
      script.remove();
      logInfo(logger, 'removeScript', 'Script tag removed');
    }
  } catch (err) {
    logError(logger, 'removeScript', 'Failed to remove script tag', err);
  }
}

/** Whether the SDK global is available on `window`. */
export function isScriptLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.Astag?.create === 'function';
}

/**
 * Injects the SDK (if needed) and resolves with `window.Astag` once it is available.
 *
 * Resolves with `null` rather than rejecting on any failure — a missing ad must never
 * surface as an unhandled rejection on the page.
 */
export function loadScript(logger: AfterShipAdsLoggerLike = console): Promise<AstagGlobal | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(null);
  }

  if (isScriptLoaded()) return Promise.resolve(window.Astag as AstagGlobal);
  if (loadPromise) return loadPromise;

  const generation = loadGeneration;

  const pending = new Promise<AstagGlobal | null>((resolve) => {
    const existing = document.getElementById(AFTERSHIP_ADS_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? injectScript(logger);

    if (!script) {
      resolve(null);
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const settle = (value: AstagGlobal | null, failure?: string) => {
      if (failure) logError(logger, 'loadScript', failure);
      if (timeoutId) clearTimeout(timeoutId);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      resolve(value);
    };

    /** Settles with the global if it is there; reports `failure` if it is not. */
    const settleIfLoaded = (failure: string) => {
      if (isScriptLoaded()) settle(window.Astag as AstagGlobal);
      else settle(null, failure);
    };

    const handleLoad = () => settleIfLoaded('Script loaded but window.Astag is unavailable');
    const handleError = () => settle(null, 'Failed to load script');

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    timeoutId = setTimeout(
      () => settleIfLoaded('Timed out waiting for window.Astag'),
      AFTERSHIP_ADS_GLOBAL_TIMEOUT_MS,
    );

    // The tag may already have executed (e.g. a second slot mounting after the first
    // finished loading) in which case no further load event is coming.
    if (isScriptLoaded()) handleLoad();
  });

  // Only a *successful* load is worth memoizing. Caching a failure would let one transient
  // CDN blip disable ads for the rest of the SPA session, since both host pages are
  // client-side routed and every later mount would get this same resolved-null promise.
  // Dropping the dead tag too — a retry that reuses it would attach listeners to an
  // element that has already fired `error`, and hang until the timeout.
  //
  // Skipped when this attempt has been superseded (consent revoked mid-load, then
  // re-granted): removeScript() works off the shared id and would take out the
  // replacement's tag.
  loadPromise = pending.then((astag) => {
    if (!astag && generation === loadGeneration) removeScript(logger);
    return astag;
  });

  return loadPromise;
}

/** Test seam: drops the memoized load promise and invalidates in-flight attempts. */
export function resetScriptLoadState(): void {
  loadPromise = null;
  loadGeneration += 1;
}
